import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';
import * as moment from 'moment';
import {
  Form,
  Switch,
} from 'antd';
import { FilterTwoTone, FilterOutlined } from '@ant-design/icons';
import {
  defaultTokenSeperators,
  sanitiseObject,
  isInvalidFilter,
} from './utils';
import { Button } from '../../atoms/Button/Button';
import { Input } from '../../atoms/Input/Input';
import DatePicker from '../../atoms/DatePicker/DatePicker';
import TimePicker from '../../atoms/TimePicker/TimePicker';
import InputNumber from '../../atoms/InputNumber/InputNumber';
import TagsInput from '../TagsInput/TagsInput';
import { Slider } from '../../atoms/Slider/Slider';
import Select from '../../atoms/Select/Select';
import FilterPopup from './FilterPopup';
import MultiSelectSearchFilter from 'src/components/pages/GenericConsignment/CustomFilters/MultiSelectSearchFilter';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { FilterType, MULTI_SELECT_TYPES } from './constants';
import useTheme from 'src/theming/useTheme';

const Option = Select.Option;

const useStyles = createUseStyles((theme: ThemeType) => ({
  mainDiv: {
    backgroundColor: theme.color.semantic.background.neutral.secondary,
    marginRight: theme.spacing[2],
    marginLeft: theme.spacing[2],
    width: '100%',
  },
  actionButtons: {
    margin: theme.spacing[0],
  },
  multipleInput: {
    minWidth: '105px',
    maxWidth: '100% !important',
  },
  multipleInputSelected: {
    '& .ant-select-selector': {
      backgroundColor: `${theme.color.semantic.accent.tan.muted} !important`,
      border: `${theme.border.width.sm} solid ${theme.color.semantic.border.strong} !important`,
      '&:hover, &:focus, &:active': {
        backgroundColor: `${theme.color.semantic.accent.tan.muted} !important`,
        border: `${theme.border.width.sm} solid ${theme.color.semantic.border.strong} !important`,
      },
      '& .ant-select-selection-item': {
        backgroundColor: `${theme.color.semantic.background.neutral.primary} !important`,
      }
    },
    '&.ant-select-focused .ant-select-selector': {
      backgroundColor: `${theme.color.semantic.accent.tan.muted} !important`,
      borderColor: `${theme.color.semantic.border.strong} !important`,
    },
    '& .ant-select-clear': {
      backgroundColor: `${theme.color.semantic.accent.tan.muted} !important`,
    },
  },
  formContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing[2],
    gap: theme.spacing[2],
    '& .ant-form-item': {
      maxWidth: '100%',
      marginRight: theme.spacing[0],
    },
    '& label': {
      fontSize: theme.typography.fontSize.xs,
      color: theme.color.semantic.text.secondary,
    },
    '& .ant-form-item-explain': {
      fontSize: theme.typography.fontSize.xs,
      width: '150px',
    },
    '& .ant-select': {
      width: '150px !important',
    },
    '& .ant-input': {
      minWidth: '60px',
    },
    '& .ant-picker, .ant-picker-range': {
      width: '215px',
    },
  },
  multiSelectFilter: {
    marginLeft: '0px',
  },
  singleInput: {
    width: '105px',
    maxWidth: '105px',
  },
  singleInputSelected: {
    backgroundColor: `${theme.color.semantic.accent.tan.muted} !important`,
    border: `${theme.border.width.sm} solid ${theme.color.semantic.border.strong} !important`,
    '&:hover, &:focus, &:active': {
      backgroundColor: `${theme.color.semantic.accent.tan.muted} !important`,
      border: `${theme.border.width.sm} solid ${theme.color.semantic.border.strong} !important`,
    },
    '& .ant-input-number-handler': {
      backgroundColor: `${theme.color.semantic.accent.tan.muted} !important`,
      borderColor: `${theme.color.semantic.border.strong} !important`,
    },
  },
  nonSelectFilters: {
    padding: `${theme.spacing[0]} ${theme.spacing[1]}`,
  },
  filterButton: {
    border: 'none',
    boxShadow: 'unset',
    color: theme.color.semantic.text.secondary,
  },
  filterIcon: {
    color: theme.color.semantic.text.secondary,
    marginRight: theme.spacing[1],
  },
  appliedFilterCount: {
    marginLeft: theme.spacing[1],
    backgroundColor: theme.color.semantic.accent.tan.muted,
    color: theme.color.semantic.text.secondary,
    borderRadius: theme.radius.full,
    padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
  },
  filterdiv: {
    fontSize: theme.typography.fontSize.sm,
    color: theme.color.semantic.text.secondary,
  },
  selectedFilter: {
    padding: `${theme.spacing[1]} ${theme.spacing[1]}`,
    borderRadius: theme.radius.sm,
    '&:hover, &:focus, &:active': {
      padding: `${theme.spacing[1]} ${theme.spacing[1]}`,
      borderRadius: theme.radius.sm,
    },
  },
  datePickerSelected: {
    backgroundColor: `${theme.color.semantic.accent.tan.muted} !important`,
    border: `${theme.border.width.sm} solid ${theme.color.semantic.border.strong} !important`,
    '&:hover, &:focus, &:active': {
      backgroundColor: `${theme.color.semantic.accent.tan.muted} !important`,
      border: `${theme.border.width.sm} solid ${theme.color.semantic.border.strong} !important`,
    },
    '& .ant-picker-clear': {
      backgroundColor: `${theme.color.semantic.accent.tan.muted} !important`,
    },
    '&.ant-picker-focused': {
      backgroundColor: `${theme.color.semantic.accent.tan.muted} !important`,
      borderColor: `${theme.color.semantic.border.strong} !important`,
    },
  },
}));

interface IProps {
  filtersToShow?: any[];
  selectedFilters: any;
  applyFilters: (filters: any) => void;
  popupFilters?: any[];
  setCurrentCustomFilters?: (filters: any[]) => void;
  existingFilters?: any[];
  showCustomColumnsOption?: boolean;
  handleCustomColumnsOptionClick?: () => void;
  resetFilters?: () => void;
}

const FilterBarPure = (props: IProps) => {
  const {
    filtersToShow,
    selectedFilters,
    applyFilters,
    popupFilters,
    setCurrentCustomFilters,
    existingFilters,
    showCustomColumnsOption,
    handleCustomColumnsOptionClick,
    resetFilters,
  } = props;
  const theme = useTheme();
  const [isPopupVisible, setPopupVisible] = React.useState(false);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const classes = useStyles();

  const getDefaultValues = () => {
    const defaultValues = {};
    filtersToShow.forEach((ele) => {
      defaultValues[ele.id] = ele.default;
    });
    return defaultValues;
  };

  React.useEffect(() => {
    form.setFieldsValue(selectedFilters);
  }, [selectedFilters]);

  const getRule = (validationKey, validationObj) => {
    switch (validationKey) {
      case 'min':
        return { min: validationObj.value, message: validationObj.message };
      case 'max':
        return { max: validationObj.value, message: validationObj.message };
      case 'regex':
        return {
          pattern: new RegExp(validationObj.value),
          message: validationObj.message,
        };
      case 'type':
        return { type: validationObj.value, message: validationObj.message };
      default:
        return undefined;
    }
  };

  const getRules = (filter) => {
    let validationsObj = filter.validation;
    const rules: any = [];
    if (
      validationsObj instanceof Object &&
      Object.keys(validationsObj).length
    ) {
      if (validationsObj.input_length instanceof Object) {
        validationsObj = {
          ...validationsObj,
          ...validationsObj.input_length,
        };
        delete validationsObj['input_length'];
      }
      const validationKeys = Object.keys(validationsObj);
      validationKeys.forEach((ele) => {
        const rule = getRule(ele, validationsObj[ele]);
        rule && rules.push(rule);
      });
      return rules;
    }
    return [];
  };

  const generateOptionsFromList = (list) => {
    const optionsList: any = [];
    if (list) {
      list.forEach((element) => {
        optionsList.push(
          <Option key={element.value} value={element.value}>
            {element.label}
          </Option>,
        );
      });
    }
    return optionsList;
  };

  const renderMultipleInput = (filter) => {
    let tokenSeparators = new Set([
      ...defaultTokenSeperators,
      ...(filter.tokenSeparators || []),
    ]);

    if (filter.ignoreDefaultSeparators) {
      tokenSeparators = filter.tokenSeparators || [];
    }

    return (
      <TagsInput
        notFoundContent={''}
        size="small"
        placeholder={filter.placeholder || ''}
        tokenSeparators={Array.from(tokenSeparators)}
        maxHeight={24}
        className={selectedFilters[filter.id] ?
          classNames(classes.multipleInput, classes.multipleInputSelected) :
          classes.multipleInput
        }
      />
    );
  };

  const applyValidFilters = (values) => {
    applyFilters(sanitiseObject({ ...selectedFilters, ...values }));
  };

  const debouncedApplyValidFilters = React.useCallback(
    debounce((values) => applyValidFilters(values), 500),
    [applyValidFilters],
  );

  const renderSingleInput = (filter) => {
    return (
      <Input
        placeholder={filter.placeholder || ''}
        size="small"
        onChange={(e) =>
          debouncedApplyValidFilters({ [filter.id]: e.target.value })
        }
        allowClear
        className={selectedFilters[filter.id] ? classNames(classes.singleInput, classes.singleInputSelected) : classes.singleInput}
      />
    );
  };

  const renderInputNumber = (filter) => {
    return (
      <InputNumber
        placeholder={filter.placeholder || ''}
        size="small"
        onChange={(value) =>
          debouncedApplyValidFilters({ [filter.id]: value })
        }
        className={selectedFilters[filter.id] ? classNames(classes.singleInput, classes.singleInputSelected) : classes.singleInput}
      />
    );
  };

  const renderMultiSelectSearch = (filter) => {
    const isSingleSelect =
      filter.mode !== 'multiple' || filter.type === FilterType.Radio;
    return (
      <MultiSelectSearchFilter
        filter={filter}
        useMultiSelect
        isSingleSelect={isSingleSelect}
        selectedFilters={selectedFilters}
        applyFilters={(filterId, values) => {
          const allFilters = {
            ...selectedFilters,
            [filterId]: values,
          };
          applyFilters(sanitiseObject(allFilters));
        }}
      />
    );
  };

  const getShowTime = (showTime, timeFormat) => {
    return showTime ? { format: timeFormat || 'HH:mm:ss' } : false;
  };

  const renderDate = (filter) => {
    if (filter.mode === 'range') {
      return (
        <DatePicker.RangePicker
          format={filter.date_format || 'YYYY-MM-DD'}
          size="small"
          ranges={{
            Today: [moment(), moment()],
            'Last 7 days': [moment().subtract(6, 'day'), moment()],
            'Last 30 days': [moment().subtract(29, 'day'), moment()],
            'This Month': [moment().startOf('month'), moment().endOf('month')],
          }}
          showTime={getShowTime(filter.show_time, filter.time_format)}
          className={selectedFilters[filter.id] ? classes.datePickerSelected : null}
        />
      );
    }
    return (
      <DatePicker
        size="small"
        format={filter.date_format || 'YYYY-MM-DD'}
        showTime={getShowTime(filter.show_time, filter.time_format)}
        className={selectedFilters[filter.id] ? classes.datePickerSelected : null}
      />
    );
  };

  const renderSlider = (filter) => {
    return (
      <Slider
        style={{ width: '100px' }}
        range
        defaultValue={[
          (filter.defaultMinValue as number) ?? 0,
          (filter.defaultMaxValue as number) ?? 0,
        ]}
        min={filter.minValue || 0}
        max={filter.maxValue || 1}
        onAfterChange={(val) => {
          form.setFieldsValue({ [filter.id]: val });
          applyValidFilters(form.getFieldsValue());
        }}
      />
    );
  };

  const renderTime = (filter) => {
    if (filter.mode === 'range') {
      return (
        <TimePicker.RangePicker
          format={filter.time_format || 'HH:mm:ss'}
          size="small"
          className={selectedFilters[filter.id] ? classes.datePickerSelected : null}
        />
      );
    }
    return (
      <TimePicker
        format={filter.time_format || 'HH:mm:ss'}
        size="small"
        className={selectedFilters[filter.id] ? classes.datePickerSelected : null}
      />
    );
  };

  const renderFilter = (filter) => {
    if (typeof filter.type !== 'string') {
      return null;
    }

    switch (filter.type.toLowerCase()) {
      case FilterType.Input:
        return filter.mode === 'multiple'
          ? renderMultipleInput(filter)
          : renderSingleInput(filter);
      case FilterType.Date:
      case FilterType.DateTime:
        return renderDate(filter);
      case FilterType.Time:
        return renderTime(filter);
      case FilterType.Select:
      case FilterType.Search:
      case FilterType.Radio:
      case FilterType.Checkbox:
        return renderMultiSelectSearch(filter);
      case FilterType.SliderRange:
        return renderSlider(filter);
      case FilterType.InputNumber:
        return renderInputNumber(filter);
      default:
        return null;
    }
  };

  const handlePopupToggle = () => {
    setPopupVisible(!isPopupVisible);
  };

  const onReset = () => {
    form.resetFields();
    applyFilters(sanitiseObject(getDefaultValues()));
  };

  const handleFilterChange = (values, allValues) => {
    // Ignore slider values, apply filters on 'onAfterChange' method
    // Related issue: https://github.com/ant-design/ant-design/issues/14119
    if (values.dotFilter) {
      return;
    }

    const fieldsChanged = Object.keys(values);
    const validChanges = fieldsChanged.filter((ele) => {
      const fieldObj = filtersToShow.filter((item) => item.id === ele)?.[0];
      return !(
        (fieldObj?.type === FilterType.Input || fieldObj?.type === FilterType.InputNumber) &&
        fieldObj?.mode !== 'multiple'
      );
    });
    validChanges?.length && applyValidFilters(values);
  };

  const isMultiSelect = (filterType) => {
    return MULTI_SELECT_TYPES.has(filterType);
  };

  const renderFilterPopup = () => {
    if (!isPopupVisible) return;
    return (
      <div style={{ width: '100%', position: 'relative' }}>
        <FilterPopup
          onFilterClose={handlePopupToggle}
          setCurrentCustomFilters={setCurrentCustomFilters}
          filtersToShow={popupFilters}
          selectedFilters={selectedFilters}
          applyFilters={applyFilters}
          existingFilters={existingFilters}
          showCustomColumnsOption={showCustomColumnsOption}
          handleCustomColumnsOptionClick={handleCustomColumnsOptionClick}
          resetFilters={resetFilters}
        />
      </div>
    )
  }

  const renderSelectedFiltersCount = () => {
    const selectedFiltersCount = Object.keys(selectedFilters || {}).length;

    if (!selectedFiltersCount) return null;
    return (
      <span className={classes.appliedFilterCount}>
        {selectedFiltersCount}
      </span>
    );
  }

  const renderFilters = () => {
    if (!filtersToShow?.length && Array.isArray(popupFilters) && popupFilters.length) {
      return (
        <div onClick={handlePopupToggle} className={classes.filterdiv}>
          <FilterOutlined className={classes.filterIcon} />
          {t('filters')}
          {selectedFilters ? renderSelectedFiltersCount() : null}
        </div>
      );
    } else {
      const isMultiSelectSearchFilter = filtersToShow.some((filter) => [
        FilterType.Select,
        FilterType.Search,
        FilterType.Radio,
        FilterType.Checkbox].includes(filter.type));

      return (
        <Form
          onValuesChange={handleFilterChange}
          className={classes.formContainer}
          form={form}
          initialValues={getDefaultValues()}
          layout="inline"
        >
          {filtersToShow.map((filter) => {
            return (
              <Form.Item
                className={
                  isInvalidFilter(form.getFieldValue(filter.id)) || isMultiSelectSearchFilter
                    ? classes.nonSelectFilters
                    : classes.selectedFilter
                }
                label={isMultiSelect(filter.type) ? undefined : filter.label}
                name={filter.id}
                key={filter.id}
                rules={getRules(filter)}
              >
                {renderFilter(filter)}
              </Form.Item>
            );
          })}
          <Form.Item className={classes.actionButtons}>
            {(Array.isArray(popupFilters) && popupFilters.length) ?
              <Button type="link" onClick={handlePopupToggle}>
                <FilterTwoTone />
                {t('more_filters')}
              </Button> : null
            }
            <Button
              type="link"
              onClick={onReset}
              danger
              style={{ marginRight: theme.spacing[1] }}
              size="small"
            >
              {t('reset_all')}
            </Button>
          </Form.Item>
        </Form>
      )
    }
  }

  return (
    <div className={classes.mainDiv}>
      {renderFilters()}
      {renderFilterPopup()}
    </div>
  );
};

export default FilterBarPure;

